import { useState, useEffect, useCallback, useRef } from 'react';
import { useAccount, useReadContract, useReadContracts, useWriteContract, useWaitForTransactionReceipt, useWatchContractEvent, usePublicClient } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import toast from 'react-hot-toast';
import { 
  Invoice, 
  InvoiceStats, 
  PaymentHistory, 
  InvoiceFormData, 
  TransactionState, 
  UseInvoiceContractReturn,
  BlockchainInvoice 
} from '@/types/invoice';
import { MEZO_CONTRACTS, INVOICE_CONTRACT_ABI } from '@/lib/mezo';
import { boarRPC } from '@/services/boar-rpc';
import { paymentMonitor, PaymentEvent } from '@/services/payment-monitor';
import { transactionStorage } from '@/services/transaction-storage';
import { invoiceStorage, DraftInvoice } from '@/services/invoice-storage';
import { invoiceMetadataStorage } from '@/services/invoice-metadata';
import { useExplorerData } from '@/hooks/useExplorerData';

export function useInvoiceContract(): UseInvoiceContractReturn {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { stats: explorerStats, fromExplorer, refresh: refreshExplorer } = useExplorerData();

  // State
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<InvoiceStats>({
    totalRevenue: 0,
    activeInvoices: 0,
    pendingAmount: 0,
    totalInvoices: 0,
    paidInvoices: 0
  });
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Transaction states
  const [createTx, setCreateTx] = useState<TransactionState>({ status: 'idle' });
  const [confirmTx, setConfirmTx] = useState<TransactionState>({ status: 'idle' });
  const [cancelTx, setCancelTx] = useState<TransactionState>({ status: 'idle' });
  
  // Track invoice ID being confirmed (for immediate UI update)
  const confirmingInvoiceIdRef = useRef<string | null>(null);
  const cancellingInvoiceIdRef = useRef<string | null>(null);
  const creatingClientNameRef = useRef<string | null>(null);
  
  // Store pending Lightning invoice data (keyed by clientName) until InvoiceCreated event
  const pendingLightningInvoicesRef = useRef<Map<string, { 
    lightningInvoice?: string; 
    paymentMethod: 'onchain' | 'lightning' | 'both';
    bitcoinPriceAtCreation?: number;
    originalUsdAmount?: number;
  }>>(new Map());
  
  // Store pending metadata (Bitcoin price, etc.) keyed by clientName until InvoiceCreated event
  const pendingMetadataRef = useRef<Map<string, {
    bitcoinPriceAtCreation?: number;
    originalUsdAmount?: number;
  }>>(new Map());
  
  const { data: totalRevenue } = useReadContract({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    functionName: 'getTotalRevenue',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConnected,
    },
  });
  
  const { data: pendingAmount } = useReadContract({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    functionName: 'getPendingAmount',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConnected,
    },
  });
  
  const { data: invoiceCountData } = useReadContract({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    functionName: 'invoiceCount',
  });
  
  // Contract writes - using v2 API
  const { writeContractAsync: createInvoiceWrite } = useWriteContract();
  const { writeContractAsync: confirmPaymentWrite } = useWriteContract();
  const { writeContractAsync: cancelInvoiceWrite } = useWriteContract();
  const { writeContractAsync: approveInvoiceWrite } = useWriteContract();
  
  // Wait for transaction receipts
  const { isLoading: isWaitingCreate, isSuccess: isCreateSuccess } = useWaitForTransactionReceipt({
    hash: createTx.status === 'success' && 'hash' in createTx ? (createTx.hash as `0x${string}`) : undefined,
  });
  
  const { isLoading: isWaitingConfirm, isSuccess: isConfirmSuccess } = useWaitForTransactionReceipt({
    hash: confirmTx.status === 'success' && 'hash' in confirmTx ? (confirmTx.hash as `0x${string}`) : undefined,
  });
  
  const { isLoading: isWaitingCancel, isSuccess: isCancelSuccess } = useWaitForTransactionReceipt({
    hash: cancelTx.status === 'success' && 'hash' in cancelTx ? (cancelTx.hash as `0x${string}`) : undefined,
  });
  
  // Track loading states
  const isCreating = createTx.status === 'pending' || isWaitingCreate;
  const isConfirming = confirmTx.status === 'pending' || isWaitingConfirm;
  const isCancelling = cancelTx.status === 'pending' || isWaitingCancel;
  
  
  // Convert blockchain invoice to frontend invoice
  const convertBlockchainInvoice = useCallback((blockchainInvoice: BlockchainInvoice, txHash?: string): Invoice => {
    const status: 'pending' | 'paid' | 'cancelled' = 
      blockchainInvoice.cancelled ? 'cancelled' :
      blockchainInvoice.paid ? 'paid' : 'pending';
    
    // Parse the amount from wei (stored as BTC on-chain)
    // Amount on blockchain is stored in wei, convert to BTC
    let btcAmount = 0;
    try {
      // Handle various formats: string, bigint, number
      const amountInWei = blockchainInvoice.amount;
      
      if (!amountInWei || amountInWei === '0' || amountInWei === '') {
        console.warn(`⚠️ Invoice ${blockchainInvoice.id} has zero or missing amount:`, amountInWei);
      } else {
        // Convert to string if needed, then to BigInt, then format from wei
        const amountStr = String(amountInWei);
        const amountBigInt = BigInt(amountStr);
        btcAmount = parseFloat(formatEther(amountBigInt));
        
        // Log conversion for debugging
        console.log(`💰 Invoice ${blockchainInvoice.id} amount conversion:`, {
          raw: amountInWei,
          string: amountStr,
          bigInt: amountBigInt.toString(),
          btc: btcAmount,
          isValid: !isNaN(btcAmount) && btcAmount > 0
        });
        
        if (isNaN(btcAmount)) {
          console.error(`❌ Invoice ${blockchainInvoice.id} amount is NaN after conversion:`, amountInWei);
          btcAmount = 0;
        } else if (btcAmount === 0) {
          console.warn(`⚠️ Invoice ${blockchainInvoice.id} converted to 0 BTC from:`, amountInWei);
        }
      }
    } catch (error) {
      console.error(`❌ Error parsing amount for invoice ${blockchainInvoice.id}:`, error, 'Raw amount:', blockchainInvoice.amount);
      btcAmount = 0;
    }
    
    // Display the originally requested amount; keep observedInboundAmount separately for transaction details
    const displayAmount = btcAmount;
    
    // Load metadata (Bitcoin price at creation, original USD amount, and Lightning fields)
    const invoiceId = blockchainInvoice.id.toString();
    const bitcoinPriceAtCreation = address ? invoiceMetadataStorage.getBitcoinPriceAtCreation(address, invoiceId) : null;
    const paymentMethod = address ? invoiceMetadataStorage.getPaymentMethod(address, invoiceId) : null;
    const lightningInvoice = address ? invoiceMetadataStorage.getLightningInvoice(address, invoiceId) : null;
    
    return {
      id: invoiceId,
      clientName: blockchainInvoice.clientName,
      clientCode: blockchainInvoice.clientCode,
      details: blockchainInvoice.description,
      amount: displayAmount, // Show requested amount; actual received is tracked in observedInboundAmount
      currency: (blockchainInvoice.currency || 'USD') as 'USD' | 'KES', // Use stored currency
      musdAmount: displayAmount * 0.98, // Mock conversion (kept for backward compatibility)
      status,
      createdAt: blockchainInvoice.createdAt > 0 
        ? new Date(blockchainInvoice.createdAt * 1000).toISOString()
        : new Date().toISOString(), // Fallback to current time if createdAt is invalid
      wallet: blockchainInvoice.bitcoinAddress,
      bitcoinAddress: blockchainInvoice.bitcoinAddress,
      creator: blockchainInvoice.creator,
      recipient: blockchainInvoice.recipient,
      paidAt: blockchainInvoice.paidAt > 0 ? new Date(blockchainInvoice.paidAt * 1000).toISOString() : undefined,
      txHash,
      expiresAt: blockchainInvoice.expiresAt > 0 
        ? new Date(blockchainInvoice.expiresAt * 1000).toISOString()
        : new Date((blockchainInvoice.createdAt * 1000) + (60 * 60 * 1000)).toISOString(), // Fallback: createdAt + 1 hour
      payToAddress: blockchainInvoice.payToAddress || blockchainInvoice.bitcoinAddress,
      requestedAmount: blockchainInvoice.amount,
      paymentMethod: paymentMethod || undefined,
      lightningInvoice: lightningInvoice || undefined,
      paymentTxHash: blockchainInvoice.paymentTxHash || undefined,
      observedInboundAmount: blockchainInvoice.observedInboundAmount || undefined,
      balanceAtCreation: blockchainInvoice.balanceAtCreation || undefined,
      bitcoinPriceAtCreation: bitcoinPriceAtCreation || undefined,
    };
  }, [address]);

  // Read all invoices from blockchain (simpler approach - like old working code)
  const { data: allBlockchainInvoices, refetch: refetchAllInvoices, error: invoicesQueryError } = useReadContract({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    functionName: 'getAllInvoices',
    chainId: 31611, // Explicitly specify Mezo testnet chain ID
    query: {
      enabled: true, // Fetch all invoices regardless of wallet connection
      retry: 5, // Increased retries
      retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 15000),
      refetchInterval: 30000, // Refetch every 30 seconds
      staleTime: 10000,
      gcTime: 300000,
    },
  });

  // Log invoice query errors for debugging
  useEffect(() => {
    if (invoicesQueryError) {
      console.error('❌ getAllInvoices query error:', {
        message: invoicesQueryError.message,
        cause: invoicesQueryError.cause,
        contract: MEZO_CONTRACTS.INVOICE_CONTRACT,
        rpc: 'https://rpc.test.mezo.org',
      });
    } else if (allBlockchainInvoices) {
      console.log('✅ Successfully fetched invoices from blockchain:', Array.isArray(allBlockchainInvoices) ? allBlockchainInvoices.length : 'non-array', 'items');
    }
  }, [invoicesQueryError, allBlockchainInvoices]);

  // Real-time: refresh on on-chain invoice events
  const notifiedCreatedRef = useRef<Set<string>>(new Set());
  const notifiedPaidRef = useRef<Set<string>>(new Set());
  const notifiedCancelledRef = useRef<Set<string>>(new Set());

  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceCreated',
      onLogs: async (logs) => {
        console.log('📬 InvoiceCreated event received:', logs);
        
        try {
          for (const log of (logs as any[])) {
            const idBigInt = (log as any).args?.id as bigint | undefined;
            const creator = (log as any).args?.creator as string | undefined;
            const clientName = (log as any).args?.clientName || 'Client';
            
            // Only process if this invoice was created by the current wallet
            if (!idBigInt || !creator || creator.toLowerCase() !== address?.toLowerCase()) {
              console.log('⏭️ Skipping invoice event - not for current wallet:', { id: idBigInt?.toString(), creator, currentAddress: address });
              continue;
            }
            
            const id = idBigInt.toString();
            console.log('🔔 Processing invoice creation for ID:', id);
            
            // Extract invoice data from event
            const amount = (log as any).args?.amount as bigint | undefined;
            const bitcoinAddress = (log as any).args?.bitcoinAddress as string | undefined;
            const payToAddress = (log as any).args?.payToAddress as string | undefined;
            const currency = (log as any).args?.currency as string | undefined;
            const expiresAtTimestamp = (log as any).args?.expiresAt as bigint | undefined;
            
            // Calculate createdAt from current time (event just fired)
            const createdAtTimestamp = Math.floor(Date.now() / 1000);
            
            // Create blockchain invoice from event data
            const blockchainInvoice: BlockchainInvoice = {
              id: Number(idBigInt),
              creator: creator,
              recipient: creator, // Same as creator for now
              amount: amount?.toString() || '0',
              description: '', // Not in event, will be fetched later
              bitcoinAddress: bitcoinAddress || '',
              clientName: clientName,
              clientCode: '', // Not in event, will be fetched later
              paid: false,
              cancelled: false,
              createdAt: createdAtTimestamp,
              paidAt: 0,
              expiresAt: expiresAtTimestamp ? Number(expiresAtTimestamp) : (createdAtTimestamp + 3600),
              payToAddress: payToAddress || bitcoinAddress || '',
              paymentTxHash: '',
              observedInboundAmount: '0',
              currency: currency || 'USD',
              balanceAtCreation: '0',
            };
            
            // Store metadata when InvoiceCreated event is received
            const invoiceId = idBigInt.toString();
            
            // Check if there's a pending Lightning invoice for this client
            const pendingLightning = pendingLightningInvoicesRef.current.get(clientName);
            if (pendingLightning && address) {
              // Store payment metadata (could be 'lightning', 'both', or 'onchain')
              invoiceMetadataStorage.setBitcoinPriceAtCreation(
                address,
                invoiceId,
                pendingLightning.bitcoinPriceAtCreation || 0,
                pendingLightning.originalUsdAmount,
                pendingLightning.paymentMethod || 'both',
                pendingLightning.lightningInvoice
              );
              console.log(`⚡ Stored ${pendingLightning.paymentMethod || 'both'} payment metadata for invoice ID:`, invoiceId);
              // Remove from pending
              pendingLightningInvoicesRef.current.delete(clientName);
            } else {
              // Store metadata for on-chain only invoices
              const pendingMetadata = pendingMetadataRef.current.get(clientName);
              if (pendingMetadata && address) {
                invoiceMetadataStorage.setBitcoinPriceAtCreation(
                  address,
                  invoiceId,
                  pendingMetadata.bitcoinPriceAtCreation || 0,
                  pendingMetadata.originalUsdAmount,
                  'onchain'
                );
                console.log('💾 Stored metadata for invoice ID:', invoiceId);
                // Remove from pending
                pendingMetadataRef.current.delete(clientName);
              }
            }
            
            // Convert and add to state immediately (optimistic update from event)
            const convertedInvoice = convertBlockchainInvoice(blockchainInvoice);
            
            setInvoices((prev) => {
              // Check if invoice already exists
              if (prev.some(inv => inv.id === convertedInvoice.id)) {
                console.log('📋 Invoice already in state, skipping duplicate');
                return prev;
              }
              console.log('✅ Adding invoice to state immediately from event:', convertedInvoice.id);
              return [convertedInvoice, ...prev].sort((a, b) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );
            });
            
            // Refetch to get full invoice data (description, clientCode, etc.)
            const refetchMultipleTimes = async () => {
              console.log('🔄 Refetching invoices after creation...');
              
              // Immediate refetch
              await refetchAllInvoices();
              
              // Refetch after short delays
              setTimeout(async () => {
                await refetchAllInvoices();
              }, 1000);
              
              setTimeout(async () => {
                await refetchAllInvoices();
              }, 3000);
              
              setTimeout(async () => {
                await refetchAllInvoices();
              }, 5000);
            };
            
            refetchMultipleTimes();
            
            // Send notification
            if (!notifiedCreatedRef.current.has(id)) {
              toast.success('Invoice created successfully');
              
              try { 
                const notificationEvent = new CustomEvent('notify', { 
                  detail: { 
                    title: 'Client has received invoice', 
                    message: `${clientName} has received the invoice and should pay within an hour`,
                    key: `invoice-created-${id}`
                  } 
                });
                window.dispatchEvent(notificationEvent);
                console.log('✅ Notification dispatched successfully for invoice:', id);
              } catch (notifyError) {
                console.error('❌ Failed to dispatch notification:', notifyError);
              }
              notifiedCreatedRef.current.add(id);
            }
          }
        } catch (error) {
          console.error('❌ Error processing InvoiceCreated event:', error);
        }
      }
  });
  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoicePaid',
    onLogs: (logs) => {
      if (address && isConnected) {
        // Only process events for invoices created by the current wallet
        try {
          for (const log of (logs as any[])) {
            // Note: InvoicePaid event doesn't have creator, so we need to check against our invoices
            // For now, refetch and let the UI update naturally
            refetchAllInvoices();
            const id = ((log as any).args?.id as bigint | undefined)?.toString() || 'unknown';
            // Check if this invoice belongs to the current wallet by refetching
            if (!notifiedPaidRef.current.has(id)) {
              toast.success('Payment confirmed on-chain');
              // Do not push a bell notification here; the bell is emitted on user confirmPayment success
              notifiedPaidRef.current.add(id);
            }
          }
        } catch {}
      }
    }
  });
  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceCancelled',
    onLogs: (logs) => {
      console.log('📬 InvoiceCancelled event received:', logs);
      if (address && isConnected) {
        // Only process events for invoices created by the current wallet
        try {
          for (const log of (logs as any[])) {
            const id = ((log as any).args?.id as bigint | undefined)?.toString() || 'unknown';
            const creator = (log as any).args?.creator as string | undefined;
            
            console.log('🔄 Processing cancellation event for invoice:', { id, creator, currentAddress: address });
            
            // Only process if this invoice was created by the current wallet
            if (creator && creator.toLowerCase() === address.toLowerCase()) {
              // Transaction confirmed on-chain - now send notifications
              refetchAllInvoices();
              toast.success('Invoice cancelled on-chain');
              
              // Send notification only when transaction confirms on-chain
              if (!notifiedCancelledRef.current.has(id)) {
                try { 
                  window.dispatchEvent(new CustomEvent('notify', { 
                    detail: { 
                      title: 'Invoice cancelled', 
                      message: `Invoice #${id} has been cancelled on-chain`,
                      key: `invoice-cancelled-${id}`
                    } 
                  })); 
                  console.log('✅ Cancellation notification sent for invoice:', id);
                  notifiedCancelledRef.current.add(id);
                } catch (notifyError) {
                  console.error('❌ Failed to send cancellation notification:', notifyError);
                }
              } else {
                console.log('⏭️ Cancellation notification already sent for invoice:', id);
              }
            } else {
              console.log('⏭️ Skipping cancellation event - not for current wallet');
            }
          }
        } catch (error) {
          console.error('❌ Error processing InvoiceCancelled event:', error);
        }
      }
    }
  });
  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceApproved',
    onLogs: () => {
      if (address && isConnected) {
        refetchAllInvoices();
        toast.success('Invoice approved');
      }
    }
  });
  
  // Note: fetchInvoiceDetails function removed as it was unused
  
  // Load ONLY on-chain invoices - no localStorage drafts (like old working code)
  useEffect(() => {
    // If blockchain data is temporarily unavailable, show empty list (not drafts)
    if (!allBlockchainInvoices) {
      console.log('⏳ Waiting for blockchain data...');
      setInvoices([]);
      return;
    }

    if (Array.isArray(allBlockchainInvoices)) {
      try {
        console.log('📋 Converting invoices from blockchain:', allBlockchainInvoices.length);
        
        const convertedInvoices = allBlockchainInvoices.map((invoice) => {
          const blockchainInvoice: BlockchainInvoice = {
            id: Number(invoice.id),
            creator: invoice.creator,
            recipient: invoice.recipient,
            amount: invoice.amount.toString(),
            description: invoice.description,
            bitcoinAddress: invoice.bitcoinAddress,
            clientName: invoice.clientName,
            clientCode: invoice.clientCode,
            paid: invoice.paid,
            cancelled: invoice.cancelled,
            createdAt: Number(invoice.createdAt),
            paidAt: Number(invoice.paidAt || 0),
            expiresAt: Number(invoice.expiresAt || 0),
            payToAddress: invoice.payToAddress || invoice.bitcoinAddress || '',
            paymentTxHash: invoice.paymentTxHash || '',
            observedInboundAmount: invoice.observedInboundAmount || '0',
            currency: invoice.currency || 'USD',
            balanceAtCreation: invoice.balanceAtCreation || '0',
          };
          
          console.log('📄 Converting invoice:', blockchainInvoice.id, 'bitcoinAddress:', blockchainInvoice.bitcoinAddress);
          
          return convertBlockchainInvoice(blockchainInvoice);
        });
        
        console.log('✅ Converted invoices:', convertedInvoices.length);
        
        // Filter invoices by current user address (like old code did with getAllInvoices)
        const userInvoices = address && isConnected
          ? convertedInvoices.filter(inv => inv.creator.toLowerCase() === address.toLowerCase())
          : [];
        
        // Remove any local drafts that match blockchain invoices (avoid duplicates)
        try {
          const draftList = invoiceStorage.listDrafts();
          const chainClientCodes = new Set(userInvoices.map(ci => ci.clientCode).filter(Boolean));
          draftList
            .filter(d => d.clientCode && chainClientCodes.has(d.clientCode))
            .forEach(d => invoiceStorage.removeDraft(d.id));
        } catch (e) {
          console.warn('Failed to prune duplicate drafts:', e);
        }
        
        // Set invoices (only blockchain invoices for current user)
        const sorted = userInvoices.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        console.log('📝 Setting invoices for user:', address, 'count:', sorted.length);
        setInvoices(sorted);
        
        // Update payment history
        const paidInvoices = convertedInvoices
          .filter(inv => inv.status === 'paid')
          .map(inv => ({
            id: `${inv.id}-ph`,
            invoiceId: inv.id,
            clientName: inv.clientName,
            amount: inv.amount,
            currency: inv.currency,
            bitcoinAddress: inv.bitcoinAddress,
            paidAt: inv.paidAt || inv.createdAt,
            txHash: inv.paymentTxHash || inv.txHash || '',
            status: 'confirmed' as const,
          }));
        setPaymentHistory(paidInvoices as PaymentHistory[]);
        
      } catch (error) {
        console.error('Error converting invoices:', error);
        setError('Failed to load invoices');
        // Don't show drafts - only show blockchain invoices
        setInvoices([]);
      }
    }
  }, [allBlockchainInvoices, convertBlockchainInvoice, address, isConnected]);

  // Refresh all data (Explorer API for stats when available, RPC for invoice list and writes)
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await refreshExplorer();
      await refetchAllInvoices();

      if (fromExplorer && explorerStats) {
        setStats({
          totalRevenue: parseFloat(formatEther(explorerStats.totalVolumeWei)),
          pendingAmount: parseFloat(formatEther(explorerStats.pendingAmountWei)),
          totalInvoices: explorerStats.totalInvoices,
          activeInvoices: explorerStats.pendingCount,
          paidInvoices: explorerStats.paidCount,
        });
      } else {
        const totalCount = invoiceCountData ? Number(invoiceCountData as unknown as bigint) : 0;
        const paidCount = invoices.filter(inv => inv.status === 'paid').length;
        const now = Date.now();
        const pendingCount = invoices.filter(inv => {
          if (inv.status !== 'pending') return false;
          if (inv.expiresAt && new Date(inv.expiresAt).getTime() <= now) return false;
          return true;
        }).length;
        const localTotalRevenue = invoices
          .filter(inv => inv.status === 'paid')
          .reduce((sum, inv) => {
            const observed = (inv as { observedInboundAmount?: string }).observedInboundAmount;
            if (observed && observed !== '0') {
              try { return sum + Number(BigInt(observed)) / 1e18; } catch { return sum + inv.amount; }
            }
            return sum + inv.amount;
          }, 0);
        const localPending = invoices
          .filter(inv => {
            if (inv.status !== 'pending') return false;
            if (inv.expiresAt && new Date(inv.expiresAt).getTime() <= now) return false;
            return true;
          })
          .reduce((sum, inv) => sum + inv.amount, 0);

        setStats({
          totalRevenue: (totalRevenue as unknown as bigint) ? parseFloat(formatEther(totalRevenue as unknown as bigint)) : localTotalRevenue,
          pendingAmount: (pendingAmount as unknown as bigint) ? parseFloat(formatEther(pendingAmount as unknown as bigint)) : localPending,
          totalInvoices: totalCount,
          activeInvoices: pendingCount,
          paidInvoices: paidCount,
        });
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  }, [refreshExplorer, refetchAllInvoices, fromExplorer, explorerStats, invoiceCountData, invoices, totalRevenue, pendingAmount, address, isConnected]);
  
  // Create invoice
  const createInvoice = useCallback(async (data: InvoiceFormData) => {
    if (!address || !isConnected) {
      toast.error('Please connect your wallet');
      return;
    }
    
    try {
      console.log('Creating invoice with data:', data);
      setCreateTx({ status: 'pending' });
      
      // Generate client code
      const clientCode = `CLT-${data.clientName.substring(0, 3).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
      
      // Snapshot balance at creation from Mezo RPC (wei) - start in background, don't block
      const payAddr = (data.payToAddress || data.bitcoinAddress || '').trim();
      let balanceSnapshotWei = '0'; // Default value, will be updated if snapshot succeeds
      
      console.log('📝 Creating invoice on-chain only (NO localStorage drafts will be created)');
      console.log('🔒 SAFEGUARD: No draft saving - invoice will ONLY exist on blockchain');
      
      // CRITICAL: Do NOT save to localStorage - all invoices must be on-chain
      // We removed all draft creation and localStorage saving
      // Invoice will appear in UI only after blockchain confirmation via InvoiceCreated event
      
      // Verify Boar RPC is ready for payment monitoring (ensure it's connected before invoice creation)
      if (payAddr && /^0x[a-fA-F0-9]{40}$/.test(payAddr)) {
        try {
          console.log('🔍 Verifying Boar RPC connection for payment monitoring...');
          await boarRPC.getAddressBalance(payAddr);
          console.log('✅ Boar RPC is ready - payment monitoring will work when marking invoice as paid');
        } catch (rpcError) {
          console.warn('⚠️ Boar RPC verification failed (payment monitoring may not work):', rpcError);
          // Don't block invoice creation, but log the warning
        }
      }
      
      // Start balance snapshot in background (non-blocking)
      const balanceSnapshotPromise = (async () => {
        try {
          if (/^0x[a-fA-F0-9]{40}$/.test(payAddr)) {
            const bal = await boarRPC.getAddressBalance(payAddr);
            // Convert hex balance to decimal string (contract expects decimal string, not hex)
            const balanceValue = typeof bal.balance === 'string' && bal.balance.startsWith('0x')
              ? BigInt(bal.balance).toString() // Convert hex to decimal string
              : String(bal.balance);
            balanceSnapshotWei = balanceValue;
            console.log('✅ Balance snapshot completed in background:', balanceSnapshotWei);
          }
        } catch (e) {
          console.warn('Failed to snapshot balance at creation; defaulting to 0', e);
        }
      })();
      
      // Try to submit to blockchain (if wallet is connected) - TRIGGER IMMEDIATELY
      if (createInvoiceWrite) {
        try {
          // Convert amount to wei immediately (no waiting)
          const amountInWei = parseEther(data.amount);
          console.log('💰 Invoice amount:', data.amount, data.currency, '→', amountInWei.toString(), 'wei');
          
          // Wait for balance snapshot with a more realistic timeout so we don't silently
          // lose the snapshot. This improves downstream payment detection reliability.
          let finalBalanceSnapshot = balanceSnapshotWei;
          try {
            await Promise.race([
              balanceSnapshotPromise,
              new Promise(resolve => setTimeout(resolve, 5000)) // Max 5s wait
            ]);
            // If snapshot completed, update the value
            finalBalanceSnapshot = balanceSnapshotWei;
            console.log('✅ Balance snapshot ready:', finalBalanceSnapshot);
          } catch (e) {
            // Use default '0' if snapshot doesn't complete in time. Downstream logic in
            // payment-monitor will treat this as “no reliable snapshot” and fall back
            // to absolute balance checks instead of silently failing.
            console.log('⚠️ Balance snapshot did not complete before timeout – using default 0 and relying on runtime balance checks');
          }
          
          console.log('Calling createInvoiceWrite with args (wallet popup will appear now):', [
            address,
            amountInWei,
            data.details,
            data.bitcoinAddress,
            data.clientName,
            clientCode,
            Math.floor(expiresAt.getTime() / 1000), // expiresAt as Unix timestamp
            data.payToAddress || data.bitcoinAddress, // payToAddress
            data.currency, // currency
            finalBalanceSnapshot // balanceAtCreation
          ]);
          
          // Mezo can reject underpriced transactions; use eth_gasPrice with a safe floor.
          const MIN_GAS_PRICE_WEI = 20_000_000n;
          let gasPriceWei = MIN_GAS_PRICE_WEI;
          try {
            const suggested = await boarRPC.getGasPriceWei();
            gasPriceWei = suggested > MIN_GAS_PRICE_WEI ? suggested : MIN_GAS_PRICE_WEI;
          } catch (e) {
            console.warn('⚠️ Failed to fetch eth_gasPrice for createInvoice, using floor:', MIN_GAS_PRICE_WEI.toString(), e);
          }
          console.log('⛽ createInvoice gasPrice (wei):', gasPriceWei.toString());

          const hash = await createInvoiceWrite({
            address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
            abi: INVOICE_CONTRACT_ABI,
            functionName: 'createInvoice',
            args: [
              address, // recipient (same as creator for now)
              amountInWei,
              data.details,
              data.bitcoinAddress,
              data.clientName,
              clientCode,
              Math.floor(expiresAt.getTime() / 1000), // expiresAt as Unix timestamp
              data.payToAddress || data.bitcoinAddress, // payToAddress
              data.currency, // currency
              finalBalanceSnapshot // balanceAtCreation
            ],
            gasPrice: gasPriceWei,
          } as any);
          
      console.log('✅ createInvoiceWrite succeeded, transaction hash:', hash);
      setCreateTx({ status: 'success', hash });
      
      // Store client name for notification when transaction confirms
      creatingClientNameRef.current = data.clientName;
      
      // CRITICAL: Do NOT save to localStorage - invoice exists ONLY on blockchain
          // Do NOT add invoice to UI optimistically - wait for blockchain confirmation
          // The InvoiceCreated event watcher will pick it up and add it to the UI
          console.log('⏳ Waiting for blockchain confirmation - invoice will appear after transaction confirms');
          console.log('🔒 VERIFIED: No localStorage draft created - invoice is on-chain only');
          
          // Store pending Lightning invoice data if Lightning invoice exists
          if (data.lightningInvoice) {
            const paymentMethod = data.paymentMethod === 'both' ? 'both' : 
                                 data.paymentMethod === 'lightning' ? 'lightning' : 'both';
            pendingLightningInvoicesRef.current.set(data.clientName, {
              lightningInvoice: data.lightningInvoice,
              paymentMethod: paymentMethod,
              bitcoinPriceAtCreation: data.bitcoinPriceAtCreation,
              originalUsdAmount: data.originalUsdAmount
            });
            console.log(`⚡ Stored pending ${paymentMethod} payment invoice for client:`, data.clientName);
          } else {
            // Store pending metadata for on-chain only invoices
            if (data.bitcoinPriceAtCreation !== undefined) {
              pendingMetadataRef.current.set(data.clientName, {
                bitcoinPriceAtCreation: data.bitcoinPriceAtCreation,
                originalUsdAmount: data.originalUsdAmount
              });
              console.log('💾 Stored pending metadata for client:', data.clientName);
            }
          }
          
        } catch (blockchainError) {
          console.error('❌ Blockchain submission failed:', blockchainError);
          // CRITICAL: Do NOT save draft to localStorage - invoices must be on-chain only
          // If blockchain fails, invoice creation fails - no localStorage fallback
          console.log('🔒 VERIFIED: No localStorage draft saved - invoice creation failed completely');
          setCreateTx({ status: 'error', error: blockchainError instanceof Error ? blockchainError.message : 'Blockchain submission failed' });
          toast.error('Failed to create invoice on blockchain. No draft saved. Please try again.');
          creatingClientNameRef.current = null; // Clear ref on error
        }
        } else {
          console.log('❌ No wallet connection - cannot create invoice');
          console.log('🔒 VERIFIED: No localStorage draft saved - wallet connection required');
          toast.error('Please connect your wallet to create invoices on blockchain.');
        }
      
    } catch (error) {
      console.error('❌ Error creating invoice:', error);
      // CRITICAL: Do NOT save to localStorage on error - invoices must be on-chain only
      console.log('🔒 VERIFIED: No localStorage draft saved - invoice creation failed');
      setCreateTx({ status: 'error', error: 'Failed to create invoice' });
      toast.error('Failed to create invoice: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }, [address, isConnected, createInvoiceWrite]);
  
  // Verify payment via Boar using HTTP balance delta only (no WS fallback)
  const verifyPaymentViaBoar = useCallback(async (invoice: Invoice) => {
    try {
      const result = await paymentMonitor.confirmInvoicePaid(invoice);
      if (result.confirmed) {
        return { success: true, amount: result.amount };
      }
      return { success: false, error: result.error || 'No payment detected' };
    } catch (error) {
      console.error('Error verifying payment:', error);
      return { success: false, error: 'Payment verification failed' };
    }
  }, []);
  
  // Confirm payment with verification
  const confirmPayment = useCallback(async (invoiceId: string) => {
    // Store original invoice state for potential rollback
    const originalInvoice = invoices.find(inv => inv.id === invoiceId);
    
    // VERIFICATION: Comprehensive invoice analysis
    const idStr = String(invoiceId);
    const isNumericId = !isNaN(Number(invoiceId)) && !isNaN(parseInt(invoiceId, 10)) && idStr === String(parseInt(invoiceId, 10));
    const startsWithDraft = idStr.startsWith('draft_');
    const startsWithPending = idStr.startsWith('pending_');
    
    console.log('🔄 Starting payment confirmation for invoice:', invoiceId);
    console.log('🔍 Invoice Analysis:', {
      invoiceId,
      invoiceIdType: typeof invoiceId,
      isNumericId,
      startsWithDraft,
      startsWithPending,
      invoiceFound: !!originalInvoice,
      invoiceIdFromInvoice: originalInvoice?.id,
      invoiceStatus: originalInvoice?.status,
      invoiceTxHash: originalInvoice?.txHash,
      hasTxHash: !!originalInvoice?.txHash,
      invoiceClientCode: originalInvoice?.clientCode
    });
    
    // Store invoice ID for immediate update when transaction confirms
    confirmingInvoiceIdRef.current = invoiceId;
    
    try {
      setConfirmTx({ status: 'pending' });
      
      // Find the invoice to get payment details
      const invoice = originalInvoice;
      if (!invoice) {
        console.error('❌ Invoice not found:', invoiceId);
        toast.error('Invoice not found');
        setConfirmTx({ status: 'error', error: 'Invoice not found' });
        return;
      }
      
      const paymentAddress = invoice.payToAddress || invoice.bitcoinAddress;
      if (!paymentAddress) {
        console.error('❌ No payment address found for invoice:', invoiceId);
        toast.error('No payment address found for this invoice');
        setConfirmTx({ status: 'error', error: 'No payment address' });
        return;
      }
      
      // Verify payment via Boar/RPC (silently, no extra modals)
      console.log('🔍 Verifying payment for invoice:', invoiceId);
      console.log('   Payment address:', paymentAddress);
      console.log('   Expected amount (BTC):', invoice.amount);
      const verificationResult = await verifyPaymentViaBoar(invoice);
      
      if (verificationResult.success) {
        console.log('✅ Payment verified via Boar RPC! Payment detected immediately.');
        
        const paymentTxHash = (verificationResult as any).transaction?.txHash || '';
        const observedAmount = verificationResult.amount || '0';
        const paymentAddress = invoice.payToAddress || invoice.bitcoinAddress;
        const storedTransaction = (verificationResult as any).transaction;
        
        // Store transaction details IMMEDIATELY after RPC/Boar verification for Payments page
        // This ensures it appears right away, even before blockchain confirmation
        if (address && paymentAddress) {
          const transactionForPayments = {
            txHash: paymentTxHash || `boar_${invoiceId}_${Date.now()}`,
            invoiceId: invoiceId,
            from: storedTransaction?.from || 'unknown',
            to: paymentAddress,
            amount: observedAmount, // Actual amount received (in wei, may be more than requested)
            blockNumber: storedTransaction?.blockNumber || 0,
            timestamp: storedTransaction?.timestamp || Date.now(),
            confirmations: storedTransaction?.confirmations || 1,
            status: 'confirmed' as const,
          };
          
          transactionStorage.addTransaction(address, transactionForPayments);
          
          console.log('✅ Transaction stored for Payments page immediately after RPC verification:', {
            txHash: transactionForPayments.txHash,
            invoiceId,
            amount: observedAmount,
            paymentTxHash,
            paymentAddress
          });
          
          // Trigger event to update Payments page
          window.dispatchEvent(new CustomEvent('transactions_updated'));
        }
        
        // ONLY handle draft invoices locally (must start with 'draft_' AND have no txHash)
        // Draft invoices have no on-chain transaction, so no wallet needed
        // IMPORTANT: If invoice has txHash OR is numeric ID, it's on-chain and requires wallet
        const isDraftInvoice = startsWithDraft && !invoice?.txHash && !isNumericId;
        
        console.log('📊 Draft Detection Logic (Mark as Paid):', {
          startsWithDraft,
          hasNoTxHash: !invoice?.txHash,
          isNotNumeric: !isNumericId,
          isDraftInvoice,
          conclusion: isDraftInvoice ? 'LOCAL DRAFT - no wallet needed' : 'ON-CHAIN - wallet required'
        });
        
        // No draft invoices - all invoices must be on-chain
        if (isDraftInvoice) {
          console.error('❌ Draft invoice detected - this should not happen. All invoices must be on-chain.');
          toast.error('Invalid invoice: Draft invoices are not supported. Please create invoices on-chain.');
          setConfirmTx({ status: 'error', error: 'Draft invoices not supported' });
          return;
        }
        
        // For ALL on-chain invoices (numeric IDs OR have txHash OR not draft_), wallet connection is REQUIRED
        console.log('📤 ON-CHAIN INVOICE - wallet popup will appear after Boar/RPC verification');
        console.log('   Invoice ID:', invoiceId);
        console.log('   Invoice ID Type:', typeof invoiceId);
        console.log('   Is Numeric ID:', isNumericId);
        console.log('   Invoice status:', invoice?.status);
        console.log('   Has txHash:', !!invoice?.txHash);
        console.log('   Is draft_ prefix:', startsWithDraft, 'but isDraftInvoice:', isDraftInvoice, '-> requires wallet');
        
        if (!address || !isConnected) {
          console.error('❌ Wallet not connected');
          toast.error('Please connect your wallet to confirm blockchain payment');
          setConfirmTx({ status: 'error', error: 'Wallet not connected' });
          return;
        }
        
        if (!confirmPaymentWrite) {
          console.error('❌ confirmPaymentWrite function not available');
          toast.error('Failed to initialize contract write. Please refresh the page.');
          setConfirmTx({ status: 'error', error: 'Contract write not available' });
          return;
        }
        
        console.log('✅ All checks passed - proceeding to wallet write');
        console.log('📤 Confirming payment on blockchain... Wallet popup will appear now');
        toast.loading('Confirming payment on blockchain... Wallet will prompt for signature.', { id: 'confirming-blockchain' });
        
        try {
          // Convert invoice ID to BigInt - handle both string and number formats
          let invoiceIdBigInt: bigint;
          try {
            if (typeof invoiceId === 'string') {
              const parsed = parseInt(invoiceId, 10);
              if (isNaN(parsed)) {
                throw new Error(`Cannot parse invoice ID: ${invoiceId}`);
              }
              invoiceIdBigInt = BigInt(parsed);
            } else if (typeof invoiceId === 'number') {
              invoiceIdBigInt = BigInt(invoiceId);
            } else {
              invoiceIdBigInt = BigInt(invoiceId);
            }
          } catch (parseError) {
            console.error('❌ Invalid invoice ID for BigInt:', invoiceId, parseError);
            throw new Error(`Invalid invoice ID format: ${invoiceId}`);
          }
          
          // CRITICAL: Log before wallet write to verify we reach this point
          console.log('🚨 ABOUT TO CALL WALLET WRITE FOR CONFIRM PAYMENT - THIS SHOULD TRIGGER POPUP');
          console.log('   confirmPaymentWrite available:', typeof confirmPaymentWrite === 'function');
          console.log('   Contract address:', MEZO_CONTRACTS.INVOICE_CONTRACT);
          console.log('   Function: confirmPayment');
          console.log('   Invoice ID (BigInt):', invoiceIdBigInt.toString());
          console.log('   Payment TxHash:', paymentTxHash || '0x');
          console.log('   Observed Amount:', observedAmount);
          console.log('   Wallet connected:', isConnected);
          console.log('   Wallet address:', address);
          
          // Mezo enforces a minimum/global fee; some wallets submit underpriced txs.
          // Use RPC-suggested gas price (with a floor) to avoid "insufficient fee" reverts.
          const MIN_GAS_PRICE_WEI = 20_000_000n; // > 16,358,160 wei minimum seen in errors
          let gasPriceWei = MIN_GAS_PRICE_WEI;
          try {
            const suggested = await boarRPC.getGasPriceWei();
            gasPriceWei = suggested > MIN_GAS_PRICE_WEI ? suggested : MIN_GAS_PRICE_WEI;
          } catch (e) {
            console.warn('⚠️ Failed to fetch eth_gasPrice, using floor:', MIN_GAS_PRICE_WEI.toString(), e);
          }
          console.log('⛽ Using gasPrice (wei):', gasPriceWei.toString());

          console.log('📤 Calling confirmPaymentWrite NOW - WALLET POPUP SHOULD APPEAR IMMEDIATELY');
          const txHash = await confirmPaymentWrite({
            address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
            abi: INVOICE_CONTRACT_ABI,
            functionName: 'confirmPayment',
            args: [
              invoiceIdBigInt,
              paymentTxHash || '0x',
              observedAmount
            ],
            gasPrice: gasPriceWei,
          } as any);
          
          console.log('✅ Wallet write returned - hash received:', txHash);
          
          // Store transaction hash in confirmTx for receipt waiting
          if (txHash) {
            setConfirmTx({ status: 'success', hash: txHash });
            
            // Update the stored transaction with the blockchain confirmation hash if different
            if (paymentTxHash && txHash !== paymentTxHash) {
              console.log('📝 Updating transaction hash with blockchain confirmation:', txHash);
              // The transaction was already stored above, but we can update it if needed
              // For now, keep the original payment txHash and add the confirmation hash as metadata if needed
            }
          }
          
          toast.dismiss('confirming-blockchain');
          toast.success('Payment confirmed on blockchain! Transaction already saved from RPC detection.');
          try { window.dispatchEvent(new CustomEvent('notify', { detail: { title: 'Invoice paid', message: `Invoice #${invoiceId} confirmed on-chain` } })); } catch {}
          // Refresh will be triggered by the useEffect watching isConfirmSuccess
        } catch (blockchainError) {
          toast.dismiss('confirming-blockchain');
          console.error('❌ Blockchain confirmation error:', blockchainError);
          confirmingInvoiceIdRef.current = null; // Clear ref on error
          throw blockchainError;
        }
        
      } else {
        console.log('❌ Payment verification failed:', verificationResult.error);
        confirmingInvoiceIdRef.current = null; // Clear ref on verification failure
        
        // Check if it's a partial payment
        if (verificationResult.error === 'PARTIAL_PAYMENT') {
          toast.error('⏳ Waiting for client to complete transaction. Partial payment received.', {
            duration: 5000,
            icon: '⚠️',
          });
        } else {
          toast.error(`Payment verification failed: ${verificationResult.error || 'No payment detected. Make sure BTC was sent to the invoice address.'}`);
        }
        
        setConfirmTx({ status: 'error', error: verificationResult.error });
      }
      
    } catch (error) {
      console.error('Error confirming payment:', error);
      confirmingInvoiceIdRef.current = null; // Clear ref on any error
      setConfirmTx({ status: 'error', error: 'Failed to confirm payment' });
      toast.error('Failed to confirm payment');
    }
  }, [address, isConnected, confirmPaymentWrite, invoices, refreshData, verifyPaymentViaBoar]);
  
  // Cancel invoice - wallet popup appears immediately, no auto-cancel
  const cancelInvoice = useCallback(async (invoiceId: string) => {
    console.log('🔄 Cancel invoice called with ID:', invoiceId, 'Type:', typeof invoiceId);
    
    // Store invoice ID for notification when transaction confirms
    cancellingInvoiceIdRef.current = invoiceId;
    const invoice = invoices.find(inv => String(inv.id) === String(invoiceId));
    
    // VERIFICATION: Comprehensive invoice analysis
    const idStr = String(invoiceId);
    const isNumericId = !isNaN(Number(invoiceId)) && !isNaN(parseInt(invoiceId, 10)) && idStr === String(parseInt(invoiceId, 10));
    const startsWithDraft = idStr.startsWith('draft_');
    const startsWithPending = idStr.startsWith('pending_');
    
    console.log('🔍 Invoice Analysis:', {
      invoiceId,
      invoiceIdType: typeof invoiceId,
      isNumericId,
      startsWithDraft,
      startsWithPending,
      invoiceFound: !!invoice,
      invoiceIdFromInvoice: invoice?.id,
      invoiceStatus: invoice?.status,
      invoiceTxHash: invoice?.txHash,
      hasTxHash: !!invoice?.txHash,
      invoiceClientCode: invoice?.clientCode
    });
    
    // Check if invoice is already cancelled
    if (invoice?.status === 'cancelled') {
      console.log('⚠️ Invoice is already cancelled');
      toast.info('Invoice is already cancelled');
      return;
    }
    
    // Check if invoice is already paid - cannot cancel paid invoices
    if (invoice?.status === 'paid') {
      console.log('⚠️ Invoice is already paid - cannot cancel');
      toast.error('Cannot cancel an invoice that has already been paid');
      return;
    }
    
    // ONLY handle draft invoices locally (must start with 'draft_' AND have no txHash)
    // Draft invoices have no on-chain transaction, so no wallet needed
    // IMPORTANT: If invoice has txHash OR is numeric ID, it's on-chain and requires wallet
    const isDraftInvoice = startsWithDraft && !invoice?.txHash && !isNumericId;
    
    console.log('📊 Draft Detection Logic:', {
      startsWithDraft,
      hasNoTxHash: !invoice?.txHash,
      isNotNumeric: !isNumericId,
      isDraftInvoice,
      conclusion: isDraftInvoice ? 'LOCAL DRAFT - no wallet needed' : 'ON-CHAIN - wallet required'
    });
    
    // No draft invoices - all invoices must be on-chain
    if (isDraftInvoice) {
      console.error('❌ Draft invoice detected - this should not happen. All invoices must be on-chain.');
      toast.error('Invalid invoice: Draft invoices are not supported. All invoices must be on-chain.');
      return;
    }

    // For ALL on-chain invoices (numeric IDs OR have txHash OR not draft_), wallet popup is REQUIRED
    console.log('📤 ON-CHAIN INVOICE - wallet popup will appear now');
    console.log('   Invoice ID:', invoiceId);
    console.log('   Invoice ID Type:', typeof invoiceId);
    console.log('   Is Numeric ID:', isNumericId);
    console.log('   Invoice status:', invoice?.status);
    console.log('   Has txHash:', !!invoice?.txHash);
    console.log('   Is draft_ prefix:', startsWithDraft, 'but isDraftInvoice:', isDraftInvoice, '-> requires wallet');
    
    if (!address || !isConnected) {
      console.error('❌ Wallet not connected');
      toast.error('Please connect your wallet to cancel this invoice on-chain');
      return;
    }

    if (!cancelInvoiceWrite) {
      console.error('❌ cancelInvoiceWrite function not available');
      toast.error('Failed to initialize contract write. Please refresh the page.');
      return;
    }
    
    // Check if the current wallet address matches the invoice creator
    // Only the invoice creator can cancel their own invoices (contract requirement)
    if (invoice?.creator && address) {
      const isCreator = invoice.creator.toLowerCase() === address.toLowerCase();
      console.log('🔍 Creator validation:', {
        invoiceCreator: invoice.creator,
        currentWallet: address,
        isCreator
      });
      
      if (!isCreator) {
        console.error('❌ Current wallet is not the invoice creator');
        toast.error('Only the invoice creator can cancel this invoice');
        return;
      }
    }
    
    console.log('✅ All checks passed - proceeding to wallet write');
    
    // Convert invoice ID to BigInt - MUST be a valid number for on-chain invoices
    let numericInvoiceId: bigint;
    try {
      // Try invoice.id first (if it's a number), then parse invoiceId string
      if (invoice?.id) {
        if (typeof invoice.id === 'number') {
          numericInvoiceId = BigInt(invoice.id);
        } else {
          // invoice.id is string, try to parse it
          const parsed = parseInt(String(invoice.id), 10);
          if (!isNaN(parsed)) {
            numericInvoiceId = BigInt(parsed);
          } else {
            // Fall back to parsing invoiceId parameter
            const parsedId = parseInt(invoiceId, 10);
            if (isNaN(parsedId)) {
              throw new Error(`Cannot parse invoice ID: ${invoiceId}`);
            }
            numericInvoiceId = BigInt(parsedId);
          }
        }
      } else {
        // No invoice found, try parsing invoiceId directly
        const parsed = parseInt(invoiceId, 10);
        if (isNaN(parsed)) {
          throw new Error(`Cannot parse invoice ID: ${invoiceId} (invoice not found in list)`);
        }
        numericInvoiceId = BigInt(parsed);
      }
      console.log('✅ Invoice ID converted to BigInt:', numericInvoiceId.toString());
    } catch (bigIntError) {
      console.error('❌ Invalid invoice ID:', invoiceId, bigIntError);
      toast.error(`Invalid invoice ID format. Cannot cancel on-chain.`);
      return;
    }
    
    // Verify invoice state directly from blockchain before attempting cancellation
    // This ensures we have the most up-to-date state and can provide accurate error messages
    console.log('🔍 publicClient available:', !!publicClient);
    
    if (!publicClient) {
      console.warn('⚠️ publicClient not available - cannot verify on-chain state');
      // Still proceed with local validation, but warn the user
    }
    
    try {
      console.log('🔍 Verifying invoice state from blockchain...');
      console.log('   Invoice ID:', numericInvoiceId.toString());
      console.log('   Current wallet:', address);
      
      // Use publicClient if available, otherwise try a different approach
      if (publicClient) {
        const onChainInvoice = await publicClient.readContract({
          address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
          abi: INVOICE_CONTRACT_ABI,
          functionName: 'getInvoice',
          args: [numericInvoiceId],
        }) as any;
        
        console.log('📋 On-chain invoice state:', {
          id: onChainInvoice.id?.toString(),
          creator: onChainInvoice.creator,
          paid: onChainInvoice.paid,
          cancelled: onChainInvoice.cancelled,
          currentWallet: address
        });
        
        // Verify creator matches current wallet
        if (onChainInvoice.creator && address) {
          const isOnChainCreator = onChainInvoice.creator.toLowerCase() === address.toLowerCase();
          console.log('🔍 Creator check:', {
            onChainCreator: onChainInvoice.creator,
            currentWallet: address,
            isMatch: isOnChainCreator
          });
          
          if (!isOnChainCreator) {
            console.error('❌ On-chain verification failed: Current wallet is not the invoice creator');
            console.error('   Invoice creator:', onChainInvoice.creator);
            console.error('   Current wallet:', address);
            toast.error(`Only the invoice creator can cancel this invoice. This invoice was created by ${onChainInvoice.creator.slice(0, 6)}...${onChainInvoice.creator.slice(-4)}`);
            return;
          }
        }
        
        // Verify invoice is not already paid
        if (onChainInvoice.paid) {
          console.error('❌ On-chain verification failed: Invoice is already paid');
          toast.error('Cannot cancel an invoice that has already been paid');
          return;
        }
        
        // Verify invoice is not already cancelled
        if (onChainInvoice.cancelled) {
          console.error('❌ On-chain verification failed: Invoice is already cancelled');
          toast.info('Invoice is already cancelled');
          return;
        }
        
        console.log('✅ On-chain verification passed - invoice can be cancelled');
      } else {
        console.warn('⚠️ Skipping on-chain verification - publicClient not available');
      }
    } catch (readError: any) {
      console.error('⚠️ Could not verify invoice state from blockchain:', readError);
      console.error('   Error message:', readError?.message);
      // Check for specific error messages
      if (readError?.message?.includes('Invalid invoice ID') || readError?.message?.includes('reverted')) {
        toast.error('Invoice not found on blockchain or invalid. It may have been deleted or never created.');
        return;
      }
      // For other errors, continue but warn
      console.warn('⚠️ Continuing without on-chain verification due to read error');
    }
    
    try {
      setCancelTx({ status: 'pending' });
      
      // CRITICAL: Log before wallet write to verify we reach this point
      console.log('🚨 ABOUT TO CALL WALLET WRITE - THIS SHOULD TRIGGER POPUP');
      console.log('   cancelInvoiceWrite available:', typeof cancelInvoiceWrite === 'function');
      console.log('   Contract address:', MEZO_CONTRACTS.INVOICE_CONTRACT);
      console.log('   Function: cancelInvoice');
      console.log('   Invoice ID (BigInt):', numericInvoiceId.toString());
      console.log('   Wallet connected:', isConnected);
      console.log('   Wallet address:', address);
      
      toast.loading('Opening wallet...', { id: 'cancel-invoice' });
      
      // IMPORTANT: This await will wait for user to approve/reject in wallet
      // Wallet popup appears here - user must confirm
      console.log('📤 Calling cancelInvoiceWrite NOW - WALLET POPUP SHOULD APPEAR IMMEDIATELY');
      const hash = await cancelInvoiceWrite({
        address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
        abi: INVOICE_CONTRACT_ABI,
        functionName: 'cancelInvoice',
        args: [numericInvoiceId],
        // Set very low gas fees for Mezo testnet
        gasPrice: 1n, // 1 wei (lowest possible)
      } as any);
      
      console.log('✅ Wallet write returned - hash received:', hash);
      
      if (!hash) {
        throw new Error('Transaction hash not received - wallet may not have confirmed');
      }
      
      // ONLY update UI AFTER wallet confirms transaction
      setInvoices(prev => prev.map(inv => 
        String(inv.id) === String(invoiceId)
          ? { ...inv, status: 'cancelled' as const }
          : inv
      ));
      
      setCancelTx({ status: 'success', hash });
      toast.dismiss('cancel-invoice');
      toast.success('Invoice cancelled successfully');
      // Notification will be sent when transaction receipt confirms (in useEffect)
      console.log('✅ Cancel transaction submitted - notification will come when receipt confirms');
      refreshData();
      
    } catch (error: any) {
      console.error('❌ Error cancelling invoice:', error);
      console.error('Error details:', error);
      setCancelTx({ status: 'error', error: error?.message || 'Failed to cancel invoice' });
      toast.dismiss('cancel-invoice');
      
      // Provide specific error messages based on error type
      const errorMessage = error?.message?.toLowerCase() || '';
      
      if (error?.code === 4001 || errorMessage.includes('reject') || errorMessage.includes('denied') || errorMessage.includes('user rejected')) {
        toast.error('Transaction cancelled by user');
      } else if (errorMessage.includes('only invoice creator') || errorMessage.includes('not authorized')) {
        toast.error('Only the invoice creator can cancel this invoice');
      } else if (errorMessage.includes('cannot cancel paid') || errorMessage.includes('already paid')) {
        toast.error('Cannot cancel an invoice that has already been paid');
      } else if (errorMessage.includes('already cancelled')) {
        toast.info('Invoice is already cancelled');
      } else if (errorMessage.includes('invalid invoice id') || errorMessage.includes('invoice not found')) {
        toast.error('Invoice not found. It may not exist on the blockchain.');
      } else if (errorMessage.includes('insufficient funds') || errorMessage.includes('gas')) {
        toast.error('Insufficient funds for gas. Please ensure you have enough ETH for transaction fees.');
      } else if (errorMessage.includes('internal json-rpc error') || errorMessage.includes('reverted')) {
        // Generic revert - likely a contract validation failure
        toast.error('Transaction failed. Please verify you are the invoice creator and the invoice has not been paid or cancelled.');
      } else {
        toast.error(`Failed to cancel invoice: ${error?.shortMessage || error?.message || 'Please check your wallet and try again'}`);
      }
      // Don't update UI on error - invoice stays as pending
      cancellingInvoiceIdRef.current = null; // Clear ref on error
    }
  }, [address, isConnected, cancelInvoiceWrite, invoices, refreshData, publicClient]);

  // Approve invoice (Board role)
  const approveInvoice = useCallback(async (invoiceId: string) => {
    if (!address || !isConnected) {
      toast.error('Please connect your wallet');
      return;
    }
    if (!approveInvoiceWrite) {
      toast.error('Failed to initialize contract write');
      return;
    }
    try {
      await approveInvoiceWrite({
        address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
        abi: INVOICE_CONTRACT_ABI,
        functionName: 'approveInvoice',
        args: [BigInt(invoiceId)],
        // Set very low gas fees for Mezo testnet
        gasPrice: 1n, // 1 wei (lowest possible)
      } as any);
      toast.success('Approval transaction sent');
    } catch (error) {
      console.error('Error approving invoice:', error);
      toast.error('Failed to approve invoice');
    }
  }, [address, isConnected, approveInvoiceWrite]);
  
  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // Manual confirmation only - notify on detection, user clicks Mark as Paid to confirm
  const notifiedDetectedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const handlePaymentDetected = async (event: PaymentEvent) => {
      if (event.type === 'payment_detected' && event.transaction) {
        console.log('💰 Payment detected event received for invoice:', event.invoiceId);
        if (!notifiedDetectedRef.current.has(event.invoiceId)) {
          toast.success('Client payment detected. Click "Mark as Paid" to confirm.');
          // Do not add bell notification for detection; keep bell for explicit user actions
          notifiedDetectedRef.current.add(event.invoiceId);
        }
      }
    };

    const handlePaymentConfirmed = (event: PaymentEvent) => {
      if (event.type === 'payment_confirmed') {
        console.log('✅ Payment confirmed for invoice:', event.invoiceId);
        // Refresh data after payment confirmation
        refreshData();
      }
    };

    // Set up payment monitor callbacks
    paymentMonitor.setCallbacks({
      onPaymentDetected: handlePaymentDetected,
      onPaymentConfirmed: handlePaymentConfirmed,
    });

    return () => {
      // Don't clear callbacks on unmount - keep them
    };
  }, [refreshData]);

  // Monitor payments for manual verification (no auto-confirmation)
  useEffect(() => {
    console.log('🔍 Setting up payment monitoring for manual verification:', invoices.length, 'invoices');
    console.log('🔍 Connected wallet address:', address);
    
    invoices.forEach(invoice => {
      console.log('Invoice:', invoice.id, 'Status:', invoice.status, 'BitcoinAddr:', invoice.bitcoinAddress);
      
      // Monitor if invoice is pending and has bitcoinAddress (for manual verification)
      const payAddr = (invoice.payToAddress || invoice.bitcoinAddress || '').trim();
      const isEvmAddress = /^0x[a-fA-F0-9]{40}$/.test(payAddr);
      if (invoice.status === 'pending' && isEvmAddress) {
        console.log('👀 Monitoring for manual verification - invoice:', invoice.id, 'Payment address:', payAddr);

        // Calculate expected amount in wei
        const expectedAmount = invoice.amount ? parseEther(invoice.amount.toString()).toString() : undefined;

        // Subscribe to monitor the invoice's payToAddress (preferred) or bitcoinAddress
        paymentMonitor.subscribeToAddress({
          address: payAddr,
          invoiceId: invoice.id,
          expectedAmount: expectedAmount,
          creator: address, // Pass creator wallet for scoped storage
        });
      }
    });

    return () => {
      // Cleanup all subscriptions when component unmounts
      invoices.forEach(invoice => {
        if (invoice.bitcoinAddress) {
          paymentMonitor.unsubscribeFromAddress(invoice.bitcoinAddress);
        }
      });
    };
  }, [invoices, address]); // Depend on invoices and address

  // Handle transaction success - refetch invoices aggressively after confirmation
  useEffect(() => {
    if (isCreateSuccess && createTx.hash) {
      console.log('✅ Transaction receipt confirmed, aggressively refetching invoices');
      console.log('   Transaction hash:', createTx.hash);
      
      // Send notification immediately when transaction confirms
      // Use a temporary key to prevent duplicates - event watcher will use the actual invoice ID
      // Only send if event watcher hasn't sent it yet (they might race)
      try {
        // Get client name from ref (stored during invoice creation)
        const clientName = creatingClientNameRef.current || 
                          invoices[invoices.length - 1]?.clientName || 
                          'Client';
        
        // Check if event watcher already sent notification by looking for recently created invoice
        // Event watcher sends notification with key `invoice-created-${id}` when it fires
        const recentInvoice = invoices.find(inv => {
          if (inv.createdAt) {
            const createdAt = new Date(inv.createdAt).getTime();
            const now = Date.now();
            return (now - createdAt) < 15000; // Within last 15 seconds
          }
          return false;
        });
        
        // If we found a recent invoice and it's already been notified, skip
        if (recentInvoice && notifiedCreatedRef.current.has(recentInvoice.id)) {
          console.log('⏭️ Skipping receipt notification - event watcher already notified for invoice:', recentInvoice.id);
          creatingClientNameRef.current = null;
          return;
        }
        
        // Use stable key format that matches event watcher (if we have invoice ID)
        // Otherwise use receipt-based key (NotificationBell will dedupe by title+message within 5s)
        const notificationKey = recentInvoice 
          ? `invoice-created-${recentInvoice.id}` 
          : `invoice-created-receipt-${createTx.hash}`;
        
        // Only send if not already notified
        if (!recentInvoice || !notifiedCreatedRef.current.has(recentInvoice.id)) {
          window.dispatchEvent(new CustomEvent('notify', { 
            detail: { 
              title: 'Invoice created successfully', 
              message: `${clientName} has received the invoice and should pay within an hour`,
              key: notificationKey
            } 
          }));
          console.log('✅ Invoice created notification sent on transaction receipt for client:', clientName);
          
          // If we have invoice ID, mark as notified to prevent event watcher duplicate
          if (recentInvoice) {
            notifiedCreatedRef.current.add(recentInvoice.id);
          }
        }
        creatingClientNameRef.current = null; // Clear ref after sending notification
      } catch (notifyError) {
        console.error('❌ Failed to send invoice created notification:', notifyError);
        creatingClientNameRef.current = null; // Clear ref on error
      }
      
      // Event watcher will also send notification with stable key when it catches up
      // NotificationBell will dedupe any duplicates within 5 seconds
      
      // Aggressively refetch multiple times - blockchain indexing can be slow
      const aggressiveRefetch = async () => {
        console.log('🔄 Immediate refetch...');
        await refetchAllInvoices();
        
        // Refetch with delays to catch slow blockchain indexing
        setTimeout(async () => {
          console.log('🔄 Refetch after 500ms...');
          await refetchAllInvoices();
        }, 500);
        
        setTimeout(async () => {
          console.log('🔄 Refetch after 1.5s...');
          await refetchAllInvoices();
        }, 1500);
        
        setTimeout(async () => {
          console.log('🔄 Refetch after 3s...');
          await refetchAllInvoices();
        }, 3000);
        
        setTimeout(async () => {
          console.log('🔄 Refetch after 5s...');
          await refetchAllInvoices();
        }, 5000);
        
        setTimeout(async () => {
          console.log('🔄 Refetch after 10s...');
          await refetchAllInvoices();
        }, 10000);
      };
      
      aggressiveRefetch();
    }
  }, [isCreateSuccess, createTx.hash, refetchAllInvoices, address, isConnected]);

  // Handle confirm payment transaction success - update invoice status immediately when transaction is confirmed
  useEffect(() => {
    if (isConfirmSuccess && confirmTx.hash) {
      console.log('✅ Payment confirmation transaction receipt confirmed, updating invoice status immediately');
      
      // Use the stored invoice ID from the ref
      const confirmedInvoiceId = confirmingInvoiceIdRef.current;
      
      if (confirmedInvoiceId) {
        // IMMEDIATE UPDATE: Mark as paid in UI as soon as transaction receipt confirms
        setInvoices(prev => prev.map(inv => 
          inv.id === confirmedInvoiceId 
            ? { ...inv, status: 'paid' as const, paidAt: new Date().toISOString() }
            : inv
        ));
        
        // Clear the ref after use
        confirmingInvoiceIdRef.current = null;
      }
      
      // Immediately refetch to get the updated invoice status from blockchain
      refetchAllInvoices();
      
      // Also refetch with delays to ensure we catch the update even if blockchain indexing is slow
      const timer1 = setTimeout(() => {
        console.log('🔄 Refetch #1 after 500ms...');
        refetchAllInvoices();
      }, 500);
      
      const timer2 = setTimeout(() => {
        console.log('🔄 Refetch #2 after 1.5s...');
        refetchAllInvoices();
      }, 1500);
      
      const timer3 = setTimeout(() => {
        console.log('🔄 Refetch #3 after 3s...');
        refetchAllInvoices();
      }, 3000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [isConfirmSuccess, confirmTx.hash, refetchAllInvoices]);

  // Handle cancel transaction success - send notification when confirmed
  useEffect(() => {
    if (isCancelSuccess && cancelTx.hash && cancellingInvoiceIdRef.current) {
      console.log('✅ Cancel transaction receipt confirmed');
      
      // Send notification immediately when cancellation transaction confirms
      const cancelledInvoiceId = cancellingInvoiceIdRef.current;
      const cancelledInvoice = invoices.find(inv => String(inv.id) === String(cancelledInvoiceId));
      
      try {
        const clientName = cancelledInvoice?.clientName || 'Client';
        const invoiceId = cancelledInvoiceId;
        
        // Use stable key matching event watcher's key for proper deduplication
        const notificationKey = `invoice-cancelled-${invoiceId}`;
        
        // Check if event watcher already sent notification
        if (!notifiedCancelledRef.current.has(invoiceId)) {
          window.dispatchEvent(new CustomEvent('notify', { 
            detail: { 
              title: 'Invoice cancelled', 
              message: `Invoice #${invoiceId} for ${clientName} has been cancelled on-chain`,
              key: notificationKey
            } 
          }));
          console.log('✅ Invoice cancelled notification sent on transaction receipt for invoice:', invoiceId);
          // Mark as notified to prevent event watcher from sending duplicate
          notifiedCancelledRef.current.add(invoiceId);
        } else {
          console.log('⏭️ Skipping receipt notification - already notified via event watcher');
        }
        cancellingInvoiceIdRef.current = null; // Clear ref after sending notification
      } catch (notifyError) {
        console.error('❌ Failed to send cancellation notification:', notifyError);
        cancellingInvoiceIdRef.current = null; // Clear ref on error
      }
      
      // Event watcher will check notifiedCancelledRef and skip if already notified
    }
  }, [isCancelSuccess, cancelTx.hash, invoices]);

  // Debug: Monitor invoices state changes
  useEffect(() => {
    console.log('📊 Invoices state updated:', invoices.length, 'invoices');
  }, [invoices]);

  // Clean up old invoices to prevent localStorage from growing too large
  useEffect(() => {
    if (address) {
      // Clear all localStorage drafts - we only use on-chain invoices
      console.log('🧹 Cleaning up localStorage - removing all drafts (on-chain only)');
      invoiceStorage.clearAllInvoices(address);
    }
  }, [address]);

  // Load data on mount
  useEffect(() => {
    refreshData();
  }, [refreshData]);
  
  return {
    // Data
    invoices,
    stats,
    paymentHistory,
    
    // Loading states
    isLoading,
    isCreating,
    isConfirming,
    isCancelling,
    
    // Transaction states
    createTx,
    confirmTx,
    cancelTx,
    
    // Actions
    createInvoice,
    confirmPayment,
    cancelInvoice,
    approveInvoice,
    refreshData,
    
    // Error handling
    error,
    clearError,
  };
}

